// src/components/AutoScrollCarousel.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HomeImageAPI } from '../services/api';

const AutoScrollCarousel = () => {
  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [carouselItems, setCarouselItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  // Fetch carousel images from backend using HomeImageAPI
  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await HomeImageAPI.getHomeImages();
        if (response.success) {
          // Map carousel images with their descriptions
          const imageKeys = ['carousel_image_1', 'carousel_image_2', 'carousel_image_3', 'carousel_image_4'];
          const items = imageKeys.map((key, index) => {
            // Get image data (could be string URL or object with description)
            const imageData = response.data[key];
            const imageUrl = typeof imageData === 'string' ? imageData : imageData?.url;
            const description = typeof imageData === 'object' ? imageData?.description : '';
            
            return {
              id: index + 1,
              title: description || `Featured Event ${index + 1}`,
              subtitle: 'Live Betting Available',
              image: imageUrl || `https://via.placeholder.com/1200x400?text=Slide+${index + 1}`,
              ctaText: 'Bet Now',
              ctaLink: '/sports',
              description: description || `Slide ${index + 1}`
            };
          });
          setCarouselItems(items);
        }
      } catch (error) {
        console.error('Error fetching carousel images:', error);
        // Fallback to default images with descriptions
        setCarouselItems([
          { 
            id: 1, 
            title: 'World Cup 2026', 
            subtitle: 'Live Betting Available', 
            image: '/assets/1.jpeg', 
            ctaText: 'Bet Now', 
            ctaLink: '/sports',
            description: 'World Cup 2026 - Live Betting Available'
          },
          { 
            id: 2, 
            title: 'Champions League', 
            subtitle: 'Best Odds Guaranteed', 
            image: '/assets/2.jpeg', 
            ctaText: 'Place Bets', 
            ctaLink: '/sports',
            description: 'Champions League - Best Odds Guaranteed'
          },
          { 
            id: 3, 
            title: 'NBA Playoffs', 
            subtitle: 'In-Play Betting', 
            image: '/assets/3.jpeg', 
            ctaText: 'View Odds', 
            ctaLink: '/sports',
            description: 'NBA Playoffs - In-Play Betting'
          },
          { 
            id: 4, 
            title: 'Grand Slam Tennis', 
            subtitle: 'Live Streaming Available', 
            image: '/assets/4.jpeg', 
            ctaText: 'Watch Live', 
            ctaLink: '/sports',
            description: 'Grand Slam Tennis - Live Streaming Available'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselImages();
  }, []);

  const scrollToSlide = useCallback((index) => {
    if (!carouselRef.current) return;
    
    const itemWidth = carouselRef.current.children[0]?.clientWidth || 0;
    if (itemWidth > 0) {
      setCurrentIndex(index);
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth'
      });
    }
  }, []);

  const nextSlide = useCallback(() => {
    const nextIndex = (currentIndex + 1) % carouselItems.length;
    scrollToSlide(nextIndex);
  }, [currentIndex, carouselItems.length, scrollToSlide]);

  const startAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      if (!isPaused) {
        nextSlide();
      }
    }, 5000);
  }, [isPaused, nextSlide]);

  useEffect(() => {
    if (carouselItems.length > 1) {
      startAutoScroll();
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [carouselItems.length, startAutoScroll]);

  const handleDotClick = useCallback((index) => {
    scrollToSlide(index);
    startAutoScroll();
  }, [scrollToSlide, startAutoScroll]);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  if (loading) {
    return (
      <section className="relative h-64 md:h-80 lg:h-96 bg-gray-800 flex items-center justify-center">
        <div className="text-gray-400">Loading carousel...</div>
      </section>
    );
  }

  if (!carouselItems.length) {
    return null;
  }

  return (
    <section 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="Featured events carousel"
    >
      <div className="carousel-container overflow-hidden">
        <div 
          ref={carouselRef} 
          className="flex snap-x snap-mandatory scroll-smooth overflow-x-auto scrollbar-hide"
          role="list"
          aria-live="polite"
        >
          {carouselItems.map((item, index) => (
            <div 
              key={item.id}
              className="flex-shrink-0 w-full snap-center relative"
              role="listitem"
              aria-label={`Slide ${index + 1} of ${carouselItems.length}: ${item.title}`}
            >
              <div className="relative h-64 md:h-80 lg:h-96">
                <img 
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/1200x400?text=Slide+${index + 1}`;
                  }}
                />
                <div 
                  className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent"
                  aria-hidden="true"
                />
                
                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="max-w-3xl mx-auto">
                    {/* Title */}
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    
                    {/* Description */}
                    {/* {item.description && (
                      <p className="text-sm md:text-base text-gray-300 mb-3 opacity-90">
                        {item.description}
                      </p>
                    )} */}
                    
                    {/* Subtitle */}
                    <p className="text-base md:text-lg text-gray-300 mb-4">
                      {item.subtitle}
                    </p>
                    
                    {/* CTA Button */}
                    <Link 
                      to={item.ctaLink}
                      className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {item.ctaText}
                    </Link>
                  </div>
                </div>

                {/* Slide Indicator */}
                <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full opacity-0 hover:opacity-100 transition-opacity">
                  <span className="text-white text-xs">
                    {index + 1} / {carouselItems.length}
                  </span>
                  <span className="text-gray-300 text-xs ml-2">
                    {item.description || item.title}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {carouselItems.length > 1 && (
        <>
          {/* Navigation Dots */}
          <div 
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-10"
            role="tablist"
            aria-label="Carousel navigation"
          >
            {carouselItems.map((_, index) => (
              <button
                key={index}
                style={{
                  width: '12px',
                  height: '12px',
                  minWidth: '6px',
                  minHeight: '6px'
                }}
                className={`rounded-full transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-white ${
                  index === currentIndex 
                    ? 'bg-blue-500 scale-125' 
                    : 'bg-gray-500/80 hover:bg-gray-400'
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-selected={index === currentIndex}
                role="tab"
              />
            ))}
          </div>
          
          
          {/* Progress Indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-900/30">
            <div 
              className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
              style={{
                width: `${(currentIndex / (carouselItems.length - 1)) * 100}%`
              }}
              aria-hidden="true"
            />
          </div>
        </>
      )}
    </section>
  );
};

export default AutoScrollCarousel;